/**
 * Persistence for the CEPI shell: one localStorage snapshot per identity
 * (chat + tabs), keyed `ld.shell.v1.<uid>:<org>` (see ``buildChatScope``).
 *
 * TENANCY — a stored conversation is tenant data (CEPI answers are org-scoped
 * server-side), so every key is scoped to WHO asked and UNDER WHICH ORG, the
 * scope is FAIL-CLOSED (no signed-in uid means no read and no write), and the
 * shell keys are purged on sign-out and uid change (``purgeShellForUid``).
 * The org component is not optional: during impersonation the uid does NOT
 * change, so a uid-only key would leave the org switch wide open.
 *
 * Impersonation start/exit does NOT purge shell keys: the org is in the key,
 * so the switched-to identity can never read the other org's snapshot. The
 * consequence is that an impersonated org's conversation and tabs stay at rest
 * in this browser's localStorage (across restarts) until the admin signs out,
 * which purges every ``ld.shell.v1.<uid>:*`` key.
 *
 * The retired per-document ``ld-chat:`` sessionStorage snapshots are no longer
 * read or written; ``purgeAllChatSnapshots`` only deletes any a previous build
 * left in the tab.
 */
import type { QAMessage } from '../types/qa';
import type { Tab } from '../shell/tabState';

export interface ChatConversationTurn {
  role: string;
  content: string;
}

/** The retired per-document snapshots — the purge target. */
const STORAGE_PREFIX = 'ld-chat:';

/**
 * Per-conversation ceiling. Answers carry citations and (when the verifier is
 * on) per-segment verdicts, so a long session is easily hundreds of KB and
 * storage only affords a few MB across every key.
 */
const MAX_SNAPSHOT_BYTES = 512 * 1024;

/**
 * The identity a conversation belongs to: ``<uid>:<org>``, or null when there
 * is no signed-in user. Null is the fail-closed signal — callers must neither
 * read nor write under it.
 *
 * ``impersonatedOrgId`` is the ``X-Impersonate-Org`` value the backend actually
 * receives, so the scope tracks the org the answers were really produced under.
 * Both parts are URI-encoded so neither can contain the ``:`` delimiter, which
 * keeps ``<uid>:<org>`` unambiguous.
 */
export function buildChatScope(
  uid: string | null | undefined,
  impersonatedOrgId: string | null | undefined,
): string | null {
  if (!uid) return null;
  const org = impersonatedOrgId ? `org-${encodeURIComponent(impersonatedOrgId)}` : 'self';
  return `${encodeURIComponent(uid)}:${org}`;
}

/** Collect keys first — removing while iterating Storage shifts the indices. */
function keysMatching(store: Storage, predicate: (key: string) => boolean): string[] {
  const matched: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && predicate(key)) matched.push(key);
  }
  return matched;
}

/**
 * Delete EVERY chat snapshot in the tab, for every identity. Call whenever the
 * identity the tab acts as changes (sign-out, a different user, impersonation
 * start/exit). Leaves unrelated keys — including ``impersonation`` itself —
 * untouched.
 */
export function purgeAllChatSnapshots(): void {
  const store = getStore();
  if (!store) return;
  try {
    keysMatching(store, (key) => key.startsWith(STORAGE_PREFIX)).forEach((key) =>
      store.removeItem(key),
    );
  } catch {
    /* best effort */
  }
}

/** sessionStorage throws outright in some privacy modes — never let that escape. */
function getStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function toIsoTimestamp(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (typeof value === 'string') return value;
  return new Date().toISOString();
}

function isPersistedTurn(value: unknown): value is ChatConversationTurn {
  if (!value || typeof value !== 'object') return false;
  const turn = value as { role?: unknown; content?: unknown };
  return typeof turn.role === 'string' && typeof turn.content === 'string';
}

function isPersistedMessage(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const msg = value as { id?: unknown; role?: unknown; content?: unknown; isLoading?: unknown };
  if (typeof msg.id !== 'string' || typeof msg.content !== 'string') return false;
  if (msg.role !== 'user' && msg.role !== 'assistant') return false;
  // Defensive: an in-flight message should never have been written, and must
  // never be handed back if an older build wrote one.
  return msg.isLoading !== true;
}

function reviveMessage(value: unknown): QAMessage {
  const { isLoading: _isLoading, timestamp, ...rest } = value as Record<string, unknown>;
  const parsed = typeof timestamp === 'string' ? new Date(timestamp) : new Date(NaN);
  return {
    ...rest,
    timestamp: Number.isNaN(parsed.getTime()) ? new Date() : parsed,
  } as QAMessage;
}

// ---------------------------------------------------------------------------
// Shell persistence (UI shell revamp). One localStorage snapshot per identity:
// chat + tabs. Key = `ld.shell.v1.` + buildChatScope(uid, org) (D8).
// ---------------------------------------------------------------------------

const SHELL_PREFIX = 'ld.shell.v1.';

/** A turn is a user message plus its reply. */
export const MAX_TURNS = 20;

export interface ShellChatPart {
  messages: QAMessage[];
  conversation: ChatConversationTurn[];
  threadId: string | null;
  trimmed: boolean;
  /** The user explicitly picked "Whole label corpus" as the CEPI scope. */
  corpusPinned?: boolean;
}

export interface ShellTabsPart {
  tabs: Tab[];
  activeTabId: string | null;
}

export interface ShellSnapshot extends ShellChatPart, ShellTabsPart {
  v: 1;
}

function getLocalStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

const shellKey = (scope: string) => `${SHELL_PREFIX}${scope}`;

/** In-flight assistant messages become an interrupted marker; the rest is kept. */
export function sanitizeForShell(messages: QAMessage[]): QAMessage[] {
  return messages.map((m) => {
    if (!m.isLoading) return m;
    const { isLoading: _l, ...rest } = m;
    return { ...rest, content: '', error: 'Response interrupted', interrupted: true } as QAMessage;
  });
}

/**
 * Conversation entries a dropped slice of messages accounts for: the hook stamps
 * ``inHistory`` on the assistant message whenever it pushes that turn's pair.
 * Cutting the conversation by THIS count, not by a fixed slice or a guess from
 * the message's shape, keeps it on the same turn boundary as the messages.
 */
function cutConversation(
  dropped: QAMessage[],
  conversation: ChatConversationTurn[],
): ChatConversationTurn[] {
  const answered = dropped.filter((m) => m.role === 'assistant' && m.inHistory).length;
  return conversation.slice(answered * 2);
}

/** Keep the last MAX_TURNS turns (a turn starts at a user message). */
export function trimToRecentTurns(
  messages: QAMessage[],
  conversation: ChatConversationTurn[],
): { messages: QAMessage[]; conversation: ChatConversationTurn[]; trimmed: boolean } {
  const userIdx: number[] = [];
  messages.forEach((m, i) => {
    if (m.role === 'user') userIdx.push(i);
  });
  if (userIdx.length <= MAX_TURNS) return { messages, conversation, trimmed: false };
  const cut = userIdx[userIdx.length - MAX_TURNS]!;
  return {
    messages: messages.slice(cut),
    conversation: cutConversation(messages.slice(0, cut), conversation),
    trimmed: true,
  };
}

function serializeShell(snap: ShellSnapshot): string {
  return JSON.stringify({
    ...snap,
    messages: snap.messages.map((m) => ({ ...m, timestamp: toIsoTimestamp(m.timestamp) })),
  });
}

/** Returns null when nothing usable is stored (or storage is unavailable). */
export function readShell(scope: string | null): ShellSnapshot | null {
  const store = getLocalStore();
  if (!store || !scope) return null;
  try {
    const raw = store.getItem(shellKey(scope));
    if (!raw) return null;
    const obj = JSON.parse(raw) as Partial<Record<keyof ShellSnapshot, unknown>> | null;
    if (!obj || typeof obj !== 'object' || obj.v !== 1) return null;
    const messages = Array.isArray(obj.messages)
      ? obj.messages.filter(isPersistedMessage).map(reviveMessage)
      : [];
    const conversation = Array.isArray(obj.conversation)
      ? obj.conversation.filter(isPersistedTurn)
      : [];
    const tabs = Array.isArray(obj.tabs)
      ? (obj.tabs as Tab[]).filter(
          (t) => t && typeof t.id === 'string' && typeof t.path === 'string',
        )
      : [];
    return {
      v: 1,
      messages,
      conversation,
      threadId: typeof obj.threadId === 'string' ? obj.threadId : null,
      trimmed: obj.trimmed === true,
      corpusPinned: obj.corpusPinned === true,
      tabs,
      activeTabId: typeof obj.activeTabId === 'string' ? obj.activeTabId : null,
    };
  } catch {
    return null;
  }
}

const EMPTY_SHELL: ShellSnapshot = {
  v: 1,
  messages: [],
  conversation: [],
  threadId: null,
  trimmed: false,
  corpusPinned: false,
  tabs: [],
  activeTabId: null,
};

function writeShell(scope: string, snap: ShellSnapshot): void {
  const store = getLocalStore();
  if (!store) return;
  try {
    let current = snap;
    let payload = serializeShell(current);
    // Oldest turns go first until the snapshot fits.
    while (payload.length > MAX_SNAPSHOT_BYTES) {
      const next = current.messages.findIndex((m, i) => i > 0 && m.role === 'user');
      if (next === -1) break;
      current = {
        ...current,
        messages: current.messages.slice(next),
        conversation: cutConversation(current.messages.slice(0, next), current.conversation),
        trimmed: true,
      };
      payload = serializeShell(current);
    }
    store.setItem(shellKey(scope), payload);
  } catch {
    /* best effort */
  }
}

export function writeShellChat(scope: string | null, chat: ShellChatPart): void {
  if (!scope) return;
  const prev = readShell(scope) ?? EMPTY_SHELL;
  const { messages, conversation, trimmed } = trimToRecentTurns(
    sanitizeForShell(chat.messages),
    chat.conversation,
  );
  writeShell(scope, {
    ...prev,
    messages,
    conversation,
    threadId: chat.threadId,
    trimmed: chat.trimmed || trimmed,
    corpusPinned: chat.corpusPinned === true,
  });
}

export function writeShellTabs(scope: string | null, tabs: ShellTabsPart): void {
  if (!scope) return;
  const prev = readShell(scope) ?? EMPTY_SHELL;
  writeShell(scope, { ...prev, tabs: tabs.tabs, activeTabId: tabs.activeTabId });
}

/** Patch only `corpusPinned` (read-modify-write, like writeShellTabs). Creates the snapshot if none exists. */
export function writeShellCorpusPinned(scope: string | null, pinned: boolean): void {
  if (!scope) return;
  const prev = readShell(scope) ?? EMPTY_SHELL;
  writeShell(scope, { ...prev, corpusPinned: pinned });
}

/** Remove every shell key for a uid (self + all impersonated orgs). The colon is the boundary. */
export function purgeShellForUid(uid: string | null | undefined): void {
  const store = getLocalStore();
  if (!store || !uid) return;
  const prefix = `${SHELL_PREFIX}${encodeURIComponent(uid)}:`;
  try {
    keysMatching(store, (key) => key.startsWith(prefix)).forEach((key) => store.removeItem(key));
  } catch {
    /* best effort */
  }
}
